import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Plus, X } from "lucide-react";

function ItemRows({ items, fields, onChange, testId }) {
  const update = (idx, key, value) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    onChange(next);
  };
  const addRow = () => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-1.5">
      {items.map((it, idx) => (
        <div key={idx} className="flex items-center gap-1.5" data-testid={`${testId}-row-${idx}`}>
          {fields.map((f) => (
            <Input
              key={f.key}
              placeholder={f.label}
              value={it[f.key] ?? ""}
              onChange={(e) => update(idx, f.key, e.target.value)}
              className={`bg-black/30 border-white/10 h-8 text-xs ${f.wide ? "flex-1" : "w-16 shrink-0"}`}
            />
          ))}
          <button data-testid={`${testId}-remove-${idx}`} onClick={() => removeRow(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <Button data-testid={`${testId}-add`} onClick={addRow} size="sm" variant="secondary" className="rounded-full gap-1.5 h-7 text-xs">
        <Plus className="w-3 h-3" /> Add
      </Button>
    </div>
  );
}

export default function SheetInventorySection({ character, patchChar }) {
  const currency = character.currency || { cp: 0, sp: 0, gp: 0, pp: 0 };
  const setCurrency = (key, val) => patchChar({ currency: { ...currency, [key]: parseInt(val, 10) || 0 } });
  const weapons = (character.inventory || []).filter((it) => it.type === "weapon");
  const toggleDrawn = (item) => {
    const next = (character.inventory || []).map((it) => (it === item ? { ...it, drawn: it.drawn === false } : it));
    patchChar({ inventory: next });
  };

  return (
    <div className="space-y-4">
      {weapons.length > 0 && (
        <div>
          <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Weapons</p>
          <div className="space-y-1.5">
            {weapons.map((w, idx) => {
              const isDrawn = w.drawn !== false;
              return (
                <div key={idx} data-testid={`inventory-weapon-${idx}`} className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-1.5">
                  <span className="text-sm">{w.name}</span>
                  <button
                    data-testid={`inventory-weapon-toggle-${idx}`}
                    onClick={() => toggleDrawn(w)}
                    className={`text-[10px] uppercase rounded-full px-2.5 py-1 shrink-0 border ${isDrawn ? "bg-[#4d7c0f]/30 border-[#4d7c0f] text-[#8fc23a]" : "bg-black/30 border-white/10 text-[#f1e0c5]/50"}`}
                  >
                    {isDrawn ? "Drawn" : "Stowed"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Readied Items</p>
        <ItemRows
          testId="readied-items"
          items={character.readied_items || []}
          fields={[{ key: "name", label: "Item name", wide: true }, { key: "bulk", label: "Bulk" }]}
          onChange={(v) => patchChar({ readied_items: v })}
        />
      </div>
      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Consumables</p>
        <ItemRows
          testId="consumables"
          items={character.consumables || []}
          fields={[{ key: "name", label: "Item name", wide: true }, { key: "qty", label: "Qty" }, { key: "bulk", label: "Bulk" }]}
          onChange={(v) => patchChar({ consumables: v })}
        />
      </div>
      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Other Items</p>
        <ItemRows
          testId="other-items"
          items={character.other_items || []}
          fields={[{ key: "name", label: "Item name", wide: true }, { key: "cost", label: "Cost" }, { key: "bulk", label: "Bulk" }]}
          onChange={(v) => patchChar({ other_items: v })}
        />
      </div>
      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Coins &amp; Wealth</p>
        <div className="grid grid-cols-4 gap-2">
          {["cp", "sp", "gp", "pp"].map((c) => (
            <div key={c} className="text-center">
              <Input
                data-testid={`currency-${c}-input`}
                type="number"
                value={currency[c] ?? 0}
                onChange={(e) => setCurrency(c, e.target.value)}
                className="bg-black/30 border-white/10 h-8 text-center text-xs"
              />
              <span className="text-[9px] uppercase text-[#D4AF37]/70">{c}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Rope</p>
        <div className="flex items-center gap-2">
          <Input
            data-testid="rope-ft-input"
            type="number"
            min={0}
            value={character.rope_ft ?? 0}
            onChange={(e) => patchChar({ rope_ft: parseInt(e.target.value, 10) || 0 })}
            className="bg-black/30 border-white/10 h-8 w-20 text-xs"
          />
          <span className="text-[10px] text-[#f1e0c5]/30">ft carried — tying someone up uses 4ft</span>
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Lockpicks</p>
        <div className="flex items-center gap-2">
          <Input
            data-testid="lockpicks-input"
            type="number"
            min={0}
            value={character.lockpicks ?? 0}
            onChange={(e) => patchChar({ lockpicks: parseInt(e.target.value, 10) || 0 })}
            className="bg-black/30 border-white/10 h-8 w-20 text-xs"
          />
          <span className="text-[10px] text-[#f1e0c5]/30">carried — one breaks only if a lock-pick attempt fails</span>
        </div>
      </div>
    </div>
  );
}
